/**
 * Server-anchored clock.
 *
 * The local wall clock is never trusted for anything in the exam: it can be
 * wrong by minutes, it can be changed mid-exam (deliberately, to buy time), and
 * it jumps when the OS syncs NTP or the laptop wakes from sleep.
 *
 * So: every value we care about is anchored to a server timestamp and advanced
 * with `performance.now()`, which is monotonic and immune to wall-clock changes.
 * Re-syncing on each poll corrects for drift.
 *
 * Two things are derived from this, and both must use the SAME origin
 * (`attempts.started_at`) so the admin can lay recordings, proctor events and
 * question visits on one timeline:
 *   - `remaining()`  — the countdown
 *   - `offsetFrom()` — a position on the master timeline
 */

export interface ClockSample {
  /** `server_now_ms` from any response that carries it. */
  serverNowMs: number;
  /** `performance.now()` read immediately BEFORE the request went out. */
  perfBefore: number;
  /** `performance.now()` read immediately AFTER the response arrived. */
  perfAfter: number;
}

export class ServerClock {
  /** Server epoch ms at the anchor moment. */
  private anchorServerMs = 0;
  /** performance.now() at the anchor moment. */
  private anchorPerf = 0;
  private synced = false;
  /** Round-trip time of the sample we anchored on, for drift display. */
  private rttMs = 0;

  /**
   * Fold in a new sample. The server generated `server_now_ms` somewhere inside
   * the round trip; the midpoint is the standard estimate and is accurate to
   * within half the RTT, which on any usable connection is well under a second.
   *
   * A sample with a worse RTT than the current anchor is ignored unless the
   * anchor is stale, so one slow request does not degrade a good measurement.
   */
  sync(sample: ClockSample): void {
    const rtt = Math.max(0, sample.perfAfter - sample.perfBefore);
    const ageMs = this.synced ? performance.now() - this.anchorPerf : Infinity;

    // Re-anchor if we have nothing, if this sample is tighter, or if the
    // current anchor is old enough that drift outweighs precision.
    if (this.synced && rtt > this.rttMs && ageMs < 60_000) return;

    this.anchorServerMs = sample.serverNowMs + rtt / 2;
    this.anchorPerf = sample.perfAfter;
    this.rttMs = rtt;
    this.synced = true;
  }

  get isSynced(): boolean {
    return this.synced;
  }

  get lastRttMs(): number {
    return this.rttMs;
  }

  /** Estimated server epoch ms, right now. */
  now(): number {
    if (!this.synced) return Date.now();
    return this.anchorServerMs + (performance.now() - this.anchorPerf);
  }

  /**
   * Position on the master timeline: ms since `attempts.started_at`.
   * Clamped at 0 — a negative offset (phone paired before the exam started)
   * would be meaningless to the admin player.
   */
  offsetFrom(startedAtMs: number | null): number {
    if (startedAtMs === null) return 0;
    return Math.max(0, Math.round(this.now() - startedAtMs));
  }

  /**
   * Milliseconds left, from a server-reported `remaining_ms` captured at a
   * known sample. Decays monotonically between polls and never goes negative.
   */
  remainingFrom(deadlineMs: number | null): number | null {
    if (deadlineMs === null) return null;
    return Math.max(0, Math.round(deadlineMs - this.now()));
  }
}

/**
 * Wraps a request so its response can be used as a clock sample, without every
 * call site having to remember the two `performance.now()` reads.
 */
export async function timed<T extends { server_now_ms: number }>(
  clock: ServerClock,
  run: () => Promise<T>,
): Promise<T> {
  const perfBefore = performance.now();
  const result = await run();
  const perfAfter = performance.now();

  if (typeof result?.server_now_ms === "number") {
    clock.sync({ serverNowMs: result.server_now_ms, perfBefore, perfAfter });
  }
  return result;
}

/** Formats ms as H:MM:SS (or M:SS under an hour) for the countdown. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
